import { dynamicImport } from "./DynamicImport.js";
import { Logger } from "./Logger.js";
import { SlotType, SlotValidOption } from "./SlotContext.js";

/**
 *  String extensions
 */

// Return the last character of the string
String.prototype.last = function () {
  return this.slice(-1);
};

// Remove and add some text at the same time
String.prototype.splice = function (idx, rem, s) {
  return this.slice(0, idx) + s + this.slice(idx + rem);
};

// Test if a character is alpha numeric or _
const StringArray =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_";

String.prototype.isAlpha = function (i) {
  return StringArray.indexOf(this[i]) != -1;
};

const DefaultOptions = {
  optimize: false,
  commentEscape: false,
  emptyLinesLimit: 0,
  abortEarly: true,
  constants: {},
  macros: {},
};

const NEW_LINE = "\n";
const PREPROCESSOR_VERSION = "1.0.2";
const IGNORE_TOKEN = "// @preproc-ignore:";
const SIGNATURE_LINE = `// @preproc-version: ${PREPROCESSOR_VERSION}`;

const isValidUrl = (str) => {
  const regex = /^(https?:\/\/)[^\s$.?#].[^\s]*$/i;
  return regex.test(str);
};

const isArrayRepresentation = (str) => {
  return /^\[\s*([^,\[\]]+\s*(,\s*[^,\[\]]+\s*)*)?\]$/.test(str);
};

const ensureValueType = (str, err) => {
  const strCheck = str?.toLowerCase();
  if (!str || strCheck == "null" || strCheck == "undefined") {
    err(`Invalid slot definition. Slot values cannot be null or undefined.`);
  }

  if (!isNaN(str) && str.trim() !== "") {
    return Number(str);
  }

  if (strCheck === "true" || strCheck === "yes") return true;
  if (strCheck === "false" || strCheck === "no") return false;

  return str?.replace(/["'/]/g, "");
};

const parseArrayFromStringLoose = (str, err) => {
  const match = str.match(/^\[(.*)\]$/);
  return match
    ? match[1].split(/\s*,\s*/).map((item) => ensureValueType(item, err))
    : [str];
};

/**
 *  ScriptingPreprocessor
 */
export class ScriptingPreprocessor {
  static _directives = {};

  constructor(opt = DefaultOptions) {
    this.options = Object.assign({ ...DefaultOptions }, opt);
    this.parseNext = this.parseNext.bind(this);
    this.next = this.next.bind(this);
    this.resetSession();
  }

  /**
   *  Get available directives
   */
  get directives() {
    return ScriptingPreprocessor._directives;
  }

  /**
   *  Reset preprocessor state
   */
  resetSession() {
    this.running = false;

    this.defines = {};
    this.postOutput = [];
    this.preOutput = [];
    this.slots = [];
    this.inspector = null;

    let c = this.options?.constants || {};
    for (let i in c) this.createConstant(i, c[i].toString(), false);

    let m = this.options?.macros || {};
    for (let i in m) this.createMacro(i, m[i]);
  }

  /**
   * Run the processor
   * @param {*} code
   * @param {*} fileName
   */
  async run(code, fileName = "main") {
    let _this = this;
    this.code = code;

    this.output = "";
    this.editorOnly = "";

    this.dynamicImportTasks = [];
    this.postOutput = [];
    this.preOutput = [];
    this.slots = [];
    this.inspector = null;


    this.emptyLines = 0;

    this.currentLine = 0;
    this.currentFile = fileName;
    this._compConst("FILE", this.currentFile);

    const date = new Date();
    this._compConst("TIME", date.toLocaleTimeString());
    this._compConst("DATE", date.toLocaleDateString());

    this.running = true;
    this._error = null;

    let lines = this.code.split(/\r?\n/);
    this.linesCount = lines.length;

    function nextLine() {
      _this._compConst("LINE", _this.currentLine + 1);
      return lines[_this.currentLine++];
    }

    this.nextLine = nextLine;
    return await this.next();
  }

  /**
   *
   */
  async next() {
    while (this.running && this.currentLine < this.linesCount) {
      this.running = this.parseNext() !== false;
    }

    if (this._error != null) {
      throw this._error;
    }

    for (const importTask of this.dynamicImportTasks) {
      const { aliasList, url } = importTask;
      await dynamicImport(url, aliasList);
    }

    this.addLine(NEW_LINE);

    for (const post of this.postOutput) {
      this.addLine(post.line);
    }

    for (const pre of this.preOutput) {
      this.insertLineAtBeginning(pre.line);
    }

    this.addLine(`SlotContext.apply(self);`);

    this.addLine(NEW_LINE + SIGNATURE_LINE);

    if (this.options.optimize == true) {
      this.output = this.minifyJS(this.output);
    }

    return { output: this.output, slots: this.slots, inspector: this.inspector, editorOnly: this.minifyJS(this.editorOnly) };
  }

  minifyJS(code) {
    return code
      .replace(/\/\*[\s\S]*?\*\/|([^\\:]|^)\/\/.*$/gm, '$1') // Remove comments
      .replace(/^\s+/gm, '') // Remove leading whitespace
      .replace(/\s+$/gm, '') // Remove trailing whitespace
      .replace(/(^[ \t]*\n)/gm, '') // Remove empty lines
      .replace(/({|])/g, '$1\r\n') // Add newline after opening brackets
      .replace(/(}|])/g, '\r\n$1') // Add newline before closing brackets
      .replace(/;/g, ';\r\n') // Add newline after semicolons
      .replace(/(\r\n)/gm, '\t'); // Add indentation
  }

  /**
   *  Append a lines to the result
   */
  addLine(line) {
    this.output += line + NEW_LINE;
    this.emptyLines = 0;
  }

  insertLineAtBeginning(line) {
    this.output = line + this.output;
  }

  /**
   * Emits an error message
   * @param {*} message
   */
  error(message, evidence) {
    if (this.options.abortEarly) this.running = false;
    message += ` (line: ${this.currentLine})`;
    this._error = new SyntaxError(message);
    this._error.line = this.currentLine;
    this._error.evidence = evidence;
    return !this.options.abortEarly;
  }

  /**
   * Emits a warning message
   * @param {*} message
   */
  warning(message) {
    let loc = { line: this.currentLine, column: 0 };
    message += ` (${loc.line}:${loc.column})`;
    Logger.warn(`Preprocessor warning: ${message}`);
    return true;
  }

  /**
   *
   */
  parseNext() {
    if (this.currentLine >= this.linesCount) return;

    let line = this.nextLine(),
      text = line.trimLeft();

    if (text.length == 0) {
      if (
        this.options.emptyLinesLimit &&
        this.emptyLines >= this.options.emptyLinesLimit
      )
        return;

      this.emptyLines++;
      return this.addLine(line);
    }

    if (this.options.commentEscape && text.startsWith("//#")) return;

    if (this.options.commentEscape && text.startsWith("/*#"))
      return this.commentEnd();

    if (text[0] != "#") return this.addLine(this.addDefines(line));

    let i = text.indexOf(" "),
      name;

    if (i != -1) {
      name = text.substr(1, i - 1);
      text = text.substr(i + 1);
    } else {
      name = text.substr(1);
    }

    this.addLine(`${IGNORE_TOKEN} ${line}`);

    const key = name.trimLeft();
    let cmd = this.directives[key];

    if (!cmd)
      return this.error(
        `Unknown preprocessor directive #${key}`,
        line.trimLeft()
      );

    if (cmd) return cmd.call(this, text);
  }

  /**
   * Add defined objects to a line
   */
  addDefines(line, withConst, withMacros) {
    let i1 = -1,
      i2;
    let d, r;

    for (let i in this.defines) {
      d = this.defines[i];

      if (d.count !== undefined && withMacros === false) continue;
      if (d.count == undefined && withConst === false) continue;

      i2 = i.length;
      i1 = -1;

      for (; ;) {
        if (!line) break;

        i1 = line.indexOf(i, i1 + 1);
        if (i1 == -1) break;

        if (line.isAlpha(i1 - 1) || line.isAlpha(i1 + i2)) continue;

        if (d.count !== undefined) r = this.addMacro(line, i1, d);
        else r = this.addConstant(line, i1, d);

        line = r.line;
        i1 = r.index;
      }
    }

    return line;
  }

  /**
   * Create a constant
   * @param {*} name
   * @param {*} value
   * @param {*} addDefines
   * @param {*} checkRedefine
   */
  createConstant(name, value, addDefines = true, checkRedefine = true) {
    if (addDefines) {
      value = this.addDefines(value);
    }

    if (checkRedefine && this.defines[name]) {
      return this.error(
        `Redefinition of ${name}`,
        `#define ${name} ${value?.trimLeft()}`
      );
    }

    this.defines[name] = {
      name: name,
      value: value,
    };
  }

  /**
   * Set a compiler constant
   * @param {*} name
   * @param {*} value
   */
  _compConst(name, value) {
    this.createConstant("__" + name + "__", value, false, false);
  }

  /**
   *
   * @param {*} expression
   * @returns
   */
  parseParameters(expression) {
    const regex = /\s*\(([^)]*)\)/;
    const match = regex.exec(expression.trim());
    if (match) {
      const paramsString = match[1].trim();
      if (paramsString === "") {
        this.error("Invalid params definition");
      }
      // const paramsArray = paramsString.split(/\s*,\s*/);
      const paramsArray = paramsString
        .split(/,(?![^\[]*\])/)
        .map((p) => p.trim());
      if (paramsArray.some((p) => p == "")) {
        this.error("Invalid params definition");
      }
      return paramsArray;
    } else {
      this.error("Invalid params definition");
    }
  }

  /**
   * Add a constant in a line
   * @param {*} line
   * @param {*} i
   * @param {*} constant
   * @returns
   */
  addConstant(line, i, constant) {
    line = line.splice(i, constant.name.length, constant.value);
    i += constant.value.length;
    return { line: line, index: i };
  }

  /**
   * Create a macro (text must have the macro arguments)
   * @param {*} name
   * @param {*} text
   * @returns
   */
  createMacro(name, text) {
    if (this.defines[name]) {
      return this.error(
        `Redefinition of macro ${name}`,
        `#define ${name}${text}`
      );
    }

    let args = [];
    let end = text.indexOf(")"),
      i1 = 1,
      i2 = 0;

    if (end == -1)
      return this.error(
        `No closing parenthesis in the #define of macro ${name}`,
        name + text
      );

    while ((i2 = text.indexOf(",", i2 + 1)) != -1 && i2 < end) {
      args.push(text.substring(i1, i2).trim());
      i1 = i2 + 1;
    }

    if (end > i1) args.push(text.substring(i1, end));

    text = text.substr(end + 1).trimLeft();
    text = this.addDefines(text, false, true);
    let breaks = [];

    for (let i = 0, l = args.length, p; i < l; i++) {
      i1 = -1;
      p = args[i];
      i2 = p.length;

      for (; ;) {
        i1 = text.indexOf(p, i1 + 1);
        if (i1 == -1) break;

        if (text.isAlpha(i1 - 1) || text.isAlpha(i1 + i2)) continue;

        breaks.push([i1, i, i2]);
      }
    }

    breaks.sort(function (a, b) {
      return a[0] - b[0];
    });

    let offset = 0,
      content = [],
      pos = [];
    let i = 0;

    for (; i < breaks.length; i++) {
      content[i] = this.addDefines(
        text.slice(offset, breaks[i][0]),
        true,
        false
      );
      offset = breaks[i][0] + breaks[i][2];
      pos[i] = breaks[i][1];
    }

    content[i] = this.addDefines(text.slice(offset));
    this.defines[name] = {
      content: content,
      count: args.length,
      pos: pos,
      name: name,
    };
  }

  /**
   * Read a line and transform macro by adding their value
   */
  addMacro(line, i, macro) {
    let m = 0,
      e = i + macro.name.length,
      s = e,
      l = 0,
      args = [];

    for (let v, l = line.length; e < l; e++) {
      v = line[e];

      if (v == "(") {
        m++;
        if (m == 1) s = e + 1;
      } else if (v == "," && m == 1) {
        args.push(line.slice(s, e));
        s = e + 1;
      } else if (v == ")") {
        if (m == 1) break;
        m--;
      } else if (v != " " && m == 0) {
        return this.error(
          `There is no openning parenthesis for macro ${macro.name}`,
          line
        );
      }
    }

    if (m != 1)
      return this.error(
        `The closing parenthesis is missing for macro ${macro.name}`,
        line
      );

    let lastarg = line.slice(s, e);
    if (lastarg.trim()) args.push(lastarg);

    if (args.length > macro.count)
      return this.error(`Too many arguments for macro ${macro.name}`, line);

    if (args.length < macro.count)
      return this.error(`Too few arguments for macro ${macro.name}`, line);

    for (let j = 0; j < macro.count; j++) args[j] = this.addDefines(args[j]);

    let str = macro.content[0];

    for (s = 0, l = macro.pos.length; s < l; s++)
      str += args[macro.pos[s]] + macro.content[s + 1];

    line = line.splice(i, e - i + 1, str);
    i += str.length;

    return { line: line, index: i };
  }

  /**
   * Go to the next #elif, #else or #endif
   */
  conditionNext(end) {
    let ifCmd = ["if", "ifdef", "ifndef"];
    let elseCmd = ["elif", "else"];

    let line,
      s,
      n = 1;

    this.vlines = [];

    while (this.currentLine < this.linesCount) {
      line = this.nextLine().trimLeft();
      this.vlines.push(`${IGNORE_TOKEN} ${line}`);

      if (line[0] != "#") {
        continue;
      }

      // accumulated blocks
      if (this.vlines) {
        for (const lv of this.vlines) {
          this.addLine(lv);
        }
      }
      this.vlines = [];

      s = line.substr(1).trimLeft().split(" ")[0];

      if (ifCmd.indexOf(s) != -1) {
        n++;
      } else if (!end && n == 1 && elseCmd.indexOf(s) != -1) {
        return this.callCondition(line);
      } else if (s == "endif") {
        n--;
        if (n == 0) {
          return;
        }
      }
    }
  }

  // Call a #else or #elif condition
  callCondition(text) {
    let split = text.substr(1).trimLeft().split(" "),
      name = split[0];
    split.shift();
    text = split.join(" ").trimLeft();
    this.directives[name].call(this, text, true);
  }

  // Go to the end of the condtion (#endif)
  conditionEnd() {
    this.conditionNext(true);
  }

  // Go to the end of a multilines comment
  commentEnd() {
    this.currentLine--;
    let line, i;
    while (this.currentLine < this.linesCount) {
      line = this.nextLine();

      if (line.indexOf("*/") != -1) break;
    }
  }
}

/**
 *  Registering preprocesor directives
 */
const registerDirective = (name, fn) => {
  ScriptingPreprocessor._directives[name] = fn;
};

/**
 *  #define directive
 */
registerDirective("define", function (text) {
  const input = text;

  let i = 0;
  while (text.isAlpha(i)) i++;

  let name = text.substr(0, i),
    isMacro = text[i] == "(";

  text = text.substr(name.length).trimLeft();

  let str = text.trimRight();
  text = "";

  while (str.last() == "\\") {
    text += str.substr(0, str.length - 1) + NEW_LINE;
    str = this.nextLine().trimRight();
  }

  text += str;
  let posBegin;
  let posEnd;

  while ((posBegin = text.indexOf("/*")) != -1) {
    posEnd = text.indexOf("*/", 1 + posBegin);
    if (posEnd == -1) posEnd = text.length;

    text = text.substring(0, posBegin) + " " + text.substring(2 + posEnd);
  }

  if ((posBegin = text.indexOf("//")) != -1)
    text = text.substring(0, posBegin) + " ";

  text.trimRight();

  if (isMacro) this.createMacro(name, text);
  else this.createConstant(name, text);
});

/**
 * #undef directive
 */
registerDirective("undef", function (text) {
  let i = 0;
  while (text.isAlpha(i)) i++;

  let name = text.substr(0, i);
  delete this.defines[name];
});

/**
 *  #if directive. Use along with the defined() keyword.
 */
registerDirective("if", function (expr) {
  let _this = this;
  const originalExpr = expr;

  expr = expr.replace(/defined\s*\(\s*([\s\S]+?)\s*\)/g, function (match, p1) {
    return _this.defines[p1] === undefined ? "false" : "true";
  });

  expr = this.addDefines(expr);

  let r = null;
  try {
    r = eval(expr);
  } catch (e) {
    return this.error(`Error when evaluating #if ${originalExpr}`, expr);
  }

  if (!r) this.conditionNext();
});

/**
 * #ifdef directive (this is faster than '#if defined(VARIABLE)')
 */
registerDirective("ifdef", function (text) {
  let name = text.split(" ")[0];

  if (this.defines[name] === undefined) this.conditionNext();
});

/**
 * #ifndef directive (this is faster than '#if !defined(VARIABLE)')
 */
registerDirective("ifndef", function (text) {
  let name = text.split(" ")[0];
  if (this.defines[name] !== undefined) this.conditionNext();
});

/**
 *  #elif directive
 */
registerDirective("elif", function (expr, called) {
  if (!called) return this.conditionEnd();
  ScriptingPreprocessor._directives.if.call(this, expr);
});

/**
 *  #else directive
 */
registerDirective("else", function (expr, called) {
  if (!called) return this.conditionEnd();
});

/**
 *  #endif directive
 */
registerDirective("endif", function (expr, called) {
  // Do nothing
});

/**
 *  #error directive
 */
registerDirective("error", function (text) {
  const validString = text?.trim().replace(/['"]+/g, "");
  this.error(`${validString}`, text);
});

/**
 *  #warning directive
 */
registerDirective("warning", function (text) {
  const validString = text?.trim().replace(/['"]+/g, "");
  this.warning(`${validString}`);
});

/**
 *  #editor_region directive
 */
registerDirective("editor_region", function (text) {
  text = text.trim();
  let line, str = "";

  let ignoreLines = [];
  while (this.currentLine < this.linesCount) {
    line = this.nextLine();

    const match = line.match(/^\s*#(?!end_editor_region\b)\w+/);
    if (match) {
      this.error(`Preprocessor directives are not allowed inside #editor_region. See detail: ${line}`);
    }

    ignoreLines.push(`${IGNORE_TOKEN} ${line}`);
    if (line.trimLeft().startsWith("#end_editor_region")) break;
    str += line + NEW_LINE;
  }

  this.editorOnly += str;

  for (const l of ignoreLines) {
    this.addLine(l);
  }

});

/**
 *  #end_editor_region directive
 */
registerDirective("end_editor_region", function () {
  // just a placeholder 
});




/**
 *  #enum C like enumeration
 */
registerDirective("enum", function (text) {
  text = text.trim();
  const enumContext = this.parseParameters(text);

  let defaultOpt = { start: 0, step: 1, hex: false, bitflag: false };
  for (const p of enumContext) {
    const [prop, value] = p.split("=");
    defaultOpt[prop?.trim()] = value?.trim();
  }

  const opt = defaultOpt;
  let line,
    str = "";

  let ignoreLines = [];
  while (this.currentLine < this.linesCount) {
    line = this.nextLine();
    ignoreLines.push(`${IGNORE_TOKEN} ${line}`);
    if (line.trimLeft().startsWith("#endenum")) break;
    str += line;
  }

  let split = str.split(",");

  if (opt.bitflag == true) {
    opt.start = 1;
  }

  for (let i = 0, l = split.length; i < l; i++) {
    const name = split[i].trim();
    let v = 0;
    if (opt.bitflag == true) {
      v = opt.start;
      opt.start <<= 1;
    } else {
      v = opt.start + i * opt.step;
    }

    const value = opt.hex ? "0x" + v.toString(16) : v.toString();
    this.createConstant(name, value);
  }

  for (const l of ignoreLines) {
    this.addLine(l);
  }
});

/**
 *  #endenum directive
 */
registerDirective("endenum", function () {
  // Do nothing
});

/**
 *  #pragma directive
 */
registerDirective("pragma", function (text) {
  text = text.trim();

  if (text.startsWith("lifecycle")) {
    const hooks = this.parseParameters(text);
    if (hooks) {
      let startupHandler = "null";
      let updateHandler = "null";
      let disposeHandler = "null";
      let observeHandler = "null";

      for (const h of hooks) {
        let [hook, alias] = h.trim().split("=");
        hook = hook?.trim();
        alias = alias?.trim();

        switch (hook) {
          case "startup":
            startupHandler = alias || "startup";
            break;
          case "update":
            updateHandler = alias || "update";
            break;
          case "observe":
            observeHandler = alias || "observe";
            break;
          case "dispose":
            disposeHandler = alias || "dispose";
            break;
          default:
            this.error(`Invalid lifecycle definition ${h}`, `#pragma ${text}`);
            break;
        }
      }
      this.postOutput.push({
        alias: "__lifecycle__",
        line: `lifecycle(${startupHandler},${updateHandler},${observeHandler},${disposeHandler});`,
      });
    } else {
      this.error(
        `Invalid lifecycle definition. At least one lifecycle hook is required`,
        `#pragma ${text}`
      );
    }
  } else if (text.startsWith("import")) {
    const importContext = this.parseParameters(text);
    for (const it of importContext) {
      const [alias, url] = it?.replace(/\s+/g, "").split("=");

      let aliasArr = parseArrayFromStringLoose(
        alias.trim(),
        this.error.bind(this)
      );

      const aliasSelector = [];
      for (const a of aliasArr) {
        aliasSelector.push(a);
      }

      if (aliasSelector.length == 0) continue;

      if (this.preOutput.some((e) => e.alias == `__import__:${alias}`)) {
        return this.error(`Duplicate import definition.`);
      }

      if (aliasSelector.length > 1) {
        this.preOutput.push({
          alias: `__import__:${alias}`,
          line: `const {${aliasSelector.join(",")}} = SharedContext.unwrap();`,
        });
      } else {
        this.preOutput.push({
          alias: `__import__:${alias}`,
          line: `const ${aliasSelector[0]} = SharedContext.get('${aliasSelector[0]}');`,
        });
      }

      // dynamic import task
      const safeUrl = url?.replace(/['"]/g, "");
      if (isValidUrl(safeUrl)) {
        this.dynamicImportTasks.push({ aliasList: aliasArr, url: safeUrl });
      }

    }
  } else if (text.startsWith("export")) {
    const exportContext = this.parseParameters(text);
    for (const it of exportContext) {
      if (this.defines[it]) {
        return this.error(
          `Duplicate symbol definition ${it}. The symbol '${it}' already exists.`
        );
      }

      if (this.postOutput.some((e) => e.alias == `__export__:${it}`)) {
        return this.error(`Duplicate symbol definition.`);
      }

      this.postOutput.push({
        alias: `__export__:${it}`,
        line: `SharedContext.set('${it}',${it});`,
      });
    }
  } else if (text.startsWith("inspector")) {
    const inspectorContext = this.parseParameters(text);
    let inspectorOptions = {};

    const inspectorValidOptions = [
      "description",
      "tag",
    ];

    for (const it of inspectorContext) {
      let [key, value] = it.trim().split("=");
      let opt = key.trim();

      if (!inspectorValidOptions.includes(opt)) {
        this.error(`Invalid inspector definition. Option ${opt} is now allowed. Valid options are (${inspectorValidOptions.join(", ")})`, text);
      }

      const v = value?.trim().replace(/['"]/g, "");
      inspectorOptions[opt] = v;
    }

    this.inspector = inspectorOptions;
  }
  else if (text.startsWith("slot")) {
    const slotContext = this.parseParameters(text);
    let slotOptions = {};

    const validateSlotOption = (op) => {
      return SlotValidOption.includes(op);
    };

    const validateInitialValue = (type, value) => {
      if (!Object.values(SlotType).includes(type)) return false;

      const srtType = typeof value;
      if (
        value &&
        (type == "Number" || type == "Boolean" || type == "String")
      ) {
        return type.toLowerCase() == srtType;
      }
      return true;
    };

    for (const it of slotContext) {
      let [key, value] = it.trim().split("=");
      let opt = key.trim();
      if (!validateSlotOption(opt)) {
        this.error(`Invalid slot definition. Slot option ${opt} is now allowed. Valid options are (${SlotValidOption.join(", ")})`, text);
      }
      let typedValue = ensureValueType(value?.trim(), this.error.bind(this));
      if (isArrayRepresentation(typedValue)) {
        typedValue = parseArrayFromStringLoose(
          typedValue.trim(),
          this.error.bind(this)
        );
      }
      slotOptions[opt] = typedValue;
    }

    const { target, type, value } = slotOptions;

    if (!validateInitialValue(type, value)) {
      this.error(
        `Invalid slot type or value definition. Valid types are (${Object.values(
          SlotType
        ).join(", ")})`,
        text
      );
    }

    if (!target || !type) {
      this.error(
        `Invalid slot definition. Slot target and type properties are required.`,
        text
      );
    }

    const slotAlias = `__slot__:${target}`;
    if (this.postOutput.some((e) => e.alias == slotAlias)) {
      return this.error(`Duplicate slot definition.`, text);
    }

    const json = JSON.stringify(slotOptions);
    this.postOutput.push({
      alias: slotAlias,
      line: `${target} = SlotContext.connectSlot(self,scene)(${json});`,
    });

    this.slots.push(slotOptions);

  } else {
    this.error(`Unknown #pragma directive`, `#pragma ${text}`);
  }
});
